import React, { JSX, useEffect, useState, useContext, useCallback, useMemo } from "react";
import { loginRequest } from "../../config/msalConfig";
import { useMsal } from "@azure/msal-react";
import { AppBar, Box, Button, Toolbar, Typography } from "@mui/material";

export interface LoginContextValue {
    getUserToken: () => string | undefined;
    loginCurrentUser: () => Promise<void>;
}

export const LoginContext = React.createContext<LoginContextValue>({
    getUserToken: () => undefined,
    loginCurrentUser: async () => undefined,
});

export const useLoginContext = () => useContext(LoginContext);

const AdminRole = "SmartAssistant.Admin";

const decodeBase64Url = (value: string): string | null => {
    if (!value) return null;
    let base64 = value.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    if (pad) {
        base64 += "=".repeat(4 - pad);
    }
    try {
        return atob(base64);
    } catch {
        return null;
    }
};

const getRolesFromToken = (token?: string): string[] => {
    if (!token) return [];
    const parts = token.split(".");
    if (parts.length < 2) return [];
    const payloadJson = decodeBase64Url(parts[1]);
    if (!payloadJson) return [];
    try {
        const payload = JSON.parse(payloadJson) as Record<string, unknown>;
        const rolesValue =
            payload.roles ??
            payload.role ??
            payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
        if (Array.isArray(rolesValue)) {
            return rolesValue.filter((r): r is string => typeof r === "string");
        }
        if (typeof rolesValue === "string") {
            return [rolesValue];
        }
        return [];
    } catch {
        return [];
    }
};

const hasRole = (token: string | undefined, role: string): boolean => {
    if (!token || !role) return false;
    return getRolesFromToken(token).some(r => r.toLowerCase() === role.toLowerCase());
};

export const LoginContextProvider = (props: { children: JSX.Element; appBarActions?: JSX.Element; appBarExtras?: JSX.Element }) => {

    const { instance, accounts } = useMsal();
    const [initialAccount, setInitialAccount] = useState(accounts[0]);
    const [msGraphToken, setMsGraphToken] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true); // Für Silent Login

    const trySilentLogin = useCallback(async () => {
        const account = initialAccount;
        if (!account) return;

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account
            });
            setMsGraphToken(tokenResponse.accessToken);
        } catch (error) {
            console.warn("Silent Token-Abruf fehlgeschlagen:", error);

            // ⬇️ Hier kommt die neue automatische loginPopup-Fallback-Logik:
            try {
                const loginResponse = await instance.loginPopup(loginRequest);
                const tokenResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: loginResponse.account
                });
                setMsGraphToken(tokenResponse.accessToken);
            } catch (popupError) {
                console.error("Automatischer loginPopup fehlgeschlagen:", popupError);
            }
        }
    }, [initialAccount, instance]);

    const login = useCallback(async () => {
        try {
            const loginResponse = await instance.loginPopup(loginRequest);
            const account = loginResponse.account;

            if (account) {
                const tokenResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account
                });
                setMsGraphToken(tokenResponse.accessToken);
            }
        } catch (error) {
            console.error("Fehler beim Login:", error);
        }
    }, [instance]);

    useEffect(() => {
        if (accounts.length > 0) {
            setInitialAccount(accounts[0]);
            trySilentLogin().finally(() => setLoading(false));
        } else {
            // Lade gespeicherte Accounts nach Browser-Neustart
            const storedAccounts = instance.getAllAccounts();
            if (storedAccounts.length > 0) {
                setInitialAccount(storedAccounts[0]);
                instance.setActiveAccount(storedAccounts[0]);
                trySilentLogin().finally(() => setLoading(false));
            } else {
                setLoading(false);
            }
        }
    }, [accounts, instance, trySilentLogin]);

    const username = initialAccount?.username ?? "";
    const isAdmin = hasRole(msGraphToken, AdminRole);
    const getUserToken = useCallback(() => msGraphToken, [msGraphToken]);
    const loginCurrentUser = useCallback(async () => {
        await login();
    }, [login]);
    const contextValue = useMemo(() => ({
        getUserToken,
        loginCurrentUser,
    }), [getUserToken, loginCurrentUser]);
    return (
        <LoginContext.Provider value={contextValue}>
            <Box display="flex" flexDirection="column" height="100vh">
                <AppBar position="static">
                    <Toolbar sx={{ justifyContent: "space-between" }}>
                        <Typography variant="h6">EnerNavi</Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                            {msGraphToken ? (
                                <>
                                    <Typography variant="body2">Angemeldet als {username}</Typography>
                                    {props.appBarExtras}
                                    {isAdmin ? props.appBarActions : null}
                                </>
                            ) : (
                                !loading && (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={loginCurrentUser}
                                    >
                                        Mit Microsoftkonto anmelden
                                    </Button>
                                )
                            )}
                        </Box>
                    </Toolbar>
                </AppBar>

                <Box flexGrow={1} p={2}>
                    {msGraphToken && props.children}
                </Box>
            </Box>
        </LoginContext.Provider>
    );
};
